function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Client-side SHA-256 for instant hashing feedback on drag-drop (spec section L).
 *
 * `crypto.subtle` is unavailable only on an insecure origin, and the build targets
 * (chrome89/edge89/firefox89/safari15, see vite.config.ts) all provide it on any HTTPS or localhost
 * origin - so the old `@noble/hashes` fallback was a whole crypto dependency for a branch that could
 * not execute. It throws rather than silently returning a wrong hash if that ever stops being true.
 */
export async function sha256Hex(buf: ArrayBuffer): Promise<string> {
  if (!globalThis.crypto?.subtle) {
    throw new Error('SHA-256 needs crypto.subtle, which requires a secure origin (https or localhost).');
  }
  const digest = await globalThis.crypto.subtle.digest('SHA-256', buf);
  return bytesToHex(new Uint8Array(digest));
}

export function toBase64(buf: ArrayBuffer): string {
  let binary = '';
  const bytes = new Uint8Array(buf);
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/** Human-readable wait time, e.g. 45 -> "45s", 150 -> "2m 30s", 3900 -> "1h 5m". */
export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return rem ? `${m}m ${rem}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  return remM ? `${h}h ${remM}m` : `${h}h`;
}

/**
 * A single human-readable queue/wait status for a task, or null when it isn't waiting. Covers both
 * the admission queue (`queued`) and the detonation-capacity wait (`queued-dynamic`).
 */
export function queueWaitLabel(task: {
  state: string;
  queuePosition?: number;
  etaSeconds?: number;
}): string | null {
  const pos = task.queuePosition ? `position ${task.queuePosition}` : null;
  const eta = typeof task.etaSeconds === 'number' && task.etaSeconds > 0
    ? `~${formatDuration(task.etaSeconds)} wait`
    : null;
  if (task.state === 'queued') {
    return ['In analysis queue', pos, eta].filter(Boolean).join(' · ');
  }
  if (task.state === 'queued-dynamic') {
    return ['Waiting for a detonation slot', pos, eta].filter(Boolean).join(' · ');
  }
  return null;
}
